import { CompanyInfo, ServiceTicket, SmsLog, SmsSettings } from '../types';
import { formatTL, STATUS_LABELS } from './storage';

export const DEFAULT_SMS_SETTINGS: SmsSettings = {
  enabled: false,
  provider: 'iletimerkezi',
  apiKey: '',
  apiHash: '',
  sender: '',
  iys: '1',
  iysList: 'BIREYSEL',
  autoSendOnCreate: false,
  autoSendOnApprovalWaiting: false,
  autoSendOnReady: false,
  autoSendOnDelivered: false,
  templates: {
    ticketCreated: 'Sayin {MUSTERI_ADI}, {SERVIS_NO} nolu {CIHAZ} servis kaydiniz alinmistir. Cihaz durum takip linkiniz: {TAKIP_LINKI} {FIRMA_ADI}',
    ticketReady: 'Sayin {MUSTERI_ADI}, {SERVIS_NO} nolu {CIHAZ} cihazinizin onarimi tamamlanmistir. Teslim alabilirsiniz. Tutar: {TUTAR} TL. Cihaz takip linkiniz: {TAKIP_LINKI} {FIRMA_ADI}',
    ticketDelivered: 'Sayin {MUSTERI_ADI}, {SERVIS_NO} nolu cihaziniz teslim edilmistir. Cihaz takip & servis formu: {TAKIP_LINKI} Bizi tercih ettiginiz icin tesekkur ederiz. {FIRMA_ADI}',
    ticketPriceApproved: 'Sayin {MUSTERI_ADI}, {SERVIS_NO} nolu {CIHAZ} cihazinizin onarim tutari {TUTAR} TL dir. Onay vermek veya durumu gormek icin: {TAKIP_LINKI} {FIRMA_ADI}',
    ticketApprovalWaiting: 'Sayin {MUSTERI_ADI}, {SERVIS_NO} nolu {CIHAZ} cihazinizin onarim tutari {TUTAR} TL dir. Onay vermek veya durumu gormek icin: {TAKIP_LINKI} {FIRMA_ADI}',
    ticketCancelled: 'Sayin {MUSTERI_ADI}, {SERVIS_NO} nolu servis kaydiniz iptal edilmistir. Detay: {TAKIP_LINKI} {FIRMA_ADI}',
    custom: '{FIRMA_ADI}: Sayin {MUSTERI_ADI}, Cihaz takip linkiniz: {TAKIP_LINKI}',
  },
};

export function getCustomerTrackingUrl(ticketId: string, phone?: string): string {
  const cleanId = (ticketId || '').trim();
  const cleanDigits = (phone || '').replace(/\D/g, '').replace(/^0+/, '').replace(/^90/, '');
  const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  
  if (cleanDigits) {
    if (!origin) {
      return `/takip?takip=${encodeURIComponent(cleanId)}&tel=${encodeURIComponent(cleanDigits)}`;
    }
    return `${origin}/takip?takip=${encodeURIComponent(cleanId)}&tel=${encodeURIComponent(cleanDigits)}`;
  }

  if (!origin) {
    return `/takip?takip=${encodeURIComponent(cleanId)}`;
  }
  return `${origin}/takip?takip=${encodeURIComponent(cleanId)}`;
}

const SMS_SETTINGS_STORAGE_KEY = 'tekservis_sms_settings';
const SMS_LOGS_STORAGE_KEY = 'tekservis_sms_logs';

export function getLocalSmsSettings(): SmsSettings {
  try {
    const raw = localStorage.getItem(SMS_SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        ...DEFAULT_SMS_SETTINGS,
        ...parsed,
        templates: {
          ...DEFAULT_SMS_SETTINGS.templates,
          ...(parsed.templates || {}),
        },
      };
    }
  } catch (e) {
    console.error('Error reading local sms settings:', e);
  }
  return { ...DEFAULT_SMS_SETTINGS };
}

export function saveLocalSmsSettings(settings: SmsSettings): void {
  try {
    localStorage.setItem(SMS_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Error saving local sms settings:', e);
  }
}

export function getLocalSmsLogs(): SmsLog[] {
  try {
    const raw = localStorage.getItem(SMS_LOGS_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.error('Error reading local sms logs:', e);
  }
  return [];
}

export function addLocalSmsLog(log: SmsLog): void {
  try {
    const current = getLocalSmsLogs();
    const updated = [log, ...current].slice(0, 200);
    localStorage.setItem(SMS_LOGS_STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Error saving local sms log:', e);
  }
}

export async function fetchServerSmsSettings(): Promise<{ settings: SmsSettings; logs: SmsLog[] }> {
  try {
    const res = await fetch(`/api.php?action=get&_t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Cache-Control': 'no-cache, no-store' },
    });
    const ct = res.headers.get('content-type') || '';
    if (res.ok && ct.includes('application/json')) {
      const data = await res.json();
      if (data && !data.error) {
        const settings = data.smsSettings ? { ...DEFAULT_SMS_SETTINGS, ...data.smsSettings, templates: { ...DEFAULT_SMS_SETTINGS.templates, ...(data.smsSettings.templates || {}) } } : getLocalSmsSettings();
        const logs = Array.isArray(data.smsLogs) ? data.smsLogs : [];
        saveLocalSmsSettings(settings);
        localStorage.setItem(SMS_LOGS_STORAGE_KEY, JSON.stringify(logs));
        return { settings, logs };
      }
    }
  } catch (e) {
    console.error('Fetch server sms settings failed, using local:', e);
  }
  return { settings: getLocalSmsSettings(), logs: getLocalSmsLogs() };
}

export async function saveServerSmsSettings(settings: SmsSettings): Promise<boolean> {
  saveLocalSmsSettings(settings);
  try {
    const res = await fetch('/api.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ smsSettings: settings }),
    });
    const ct = res.headers.get('content-type') || '';
    if (!res.ok || !ct.includes('application/json')) return false;
    const data = await res.json();
    return data?.saved === true || data?.status === 'success';
  } catch (e) {
    console.error('Save server sms settings failed:', e);
    return false;
  }
}

export function formatSmsTemplate(
  templateText: string,
  ticket: ServiceTicket,
  company: CompanyInfo
): string {
  if (!templateText) return '';
  const device = `${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim() || ticket.deviceType || 'Cihaz';
  const tutar = String(ticket.totalAmount || 0);

  // Generate tracking URL with form no and clean phone parameter
  const trackingUrl = getCustomerTrackingUrl(ticket.id, ticket.customerPhone);

  return templateText
    .replace(/\{MUSTERI_ADI\}/g, ticket.customerName || 'Musteri')
    .replace(/\{SERVIS_NO\}/g, ticket.id || '')
    .replace(/\{CIHAZ\}/g, device)
    .replace(/\{TUTAR\}/g, tutar)
    .replace(/\{FIRMA_ADI\}/g, company.name || 'Teknik Servis')
    .replace(/\{FIRMA_TEL\}/g, company.phone || '')
    .replace(/\{DURUM\}/g, STATUS_LABELS[ticket.status] || ticket.status)
    .replace(/\{ARIZA\}/g, ticket.issueDescription || '')
    .replace(/\{TAKIP_LINKI\}/g, trackingUrl || ticket.id);
}

export async function sendSmsMessage(params: {
  phone: string;
  text: string;
  ticketId?: string;
  customerName?: string;
  customApiKey?: string;
  customApiHash?: string;
  customSender?: string;
}): Promise<{ success: boolean; message: string; orderId?: string; log?: SmsLog }> {
  const settings = getLocalSmsSettings();
  const apiKey = params.customApiKey || settings.apiKey;
  const apiHash = params.customApiHash || settings.apiHash;
  const sender = params.customSender || settings.sender;

  const payload = {
    apiKey,
    apiHash,
    sender,
    text: params.text,
    numbers: [params.phone],
    ticketId: params.ticketId,
    customerName: params.customerName,
  };

  let responseData: any = null;
  let lastError = '';

  // 1. Try Node.js endpoint /api/send-sms
  try {
    const res = await fetch('/api/send-sms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await res.json();
      if (res.ok && data.success) {
        responseData = data;
      } else if (!res.ok && data.message) {
        lastError = data.message;
        responseData = data;
      }
    }
  } catch (e: any) {
    lastError = e.message || 'Node backend hatası';
  }

  // 2. If first attempt was not successful or failed, try Hostinger PHP endpoint /api.php?action=send-sms
  if (!responseData || !responseData.success) {
    try {
      const phpRes = await fetch('/api.php?action=send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const contentType = phpRes.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const phpData = await phpRes.json();
        if (phpData) {
          responseData = phpData;
        }
      }
    } catch (e: any) {
      if (!lastError) lastError = e.message || 'PHP backend hatası';
    }
  }

  if (responseData && responseData.log) {
    addLocalSmsLog(responseData.log);
  }

  if (responseData && responseData.success) {
    return responseData;
  }

  const failLog: SmsLog = (responseData && responseData.log) ? responseData.log : {
    id: 'sms_' + Date.now(),
    date: new Date().toISOString(),
    phone: params.phone,
    customerName: params.customerName,
    ticketId: params.ticketId,
    message: params.text,
    status: 'failed',
    responseMessage: responseData?.message || lastError || 'Bağlantı hatası',
  };

  if (!responseData?.log) {
    addLocalSmsLog(failLog);
  }

  return {
    success: false,
    message: responseData?.message || lastError || 'SMS gönderimi başarısız oldu.',
    log: failLog,
  };
}

export async function checkSmsBalance(customApiKey?: string, customApiHash?: string): Promise<{
  success: boolean;
  balance?: any;
  message?: string;
}> {
  const settings = getLocalSmsSettings();
  const apiKey = customApiKey || settings.apiKey;
  const apiHash = customApiHash || settings.apiHash;
  const payload = { apiKey, apiHash };

  // 1. Try Node.js endpoint
  try {
    const res = await fetch('/api/sms-balance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await res.json();
      if (res.ok && data.success) {
        return data;
      }
    }
  } catch (e: any) {}

  // 2. Try PHP endpoint on Hostinger
  try {
    const phpRes = await fetch('/api.php?action=sms-balance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const contentType = phpRes.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return await phpRes.json();
    }
  } catch (e: any) {
    return {
      success: false,
      message: e.message || 'Bakiye sorgulanamadı.',
    };
  }

  return {
    success: false,
    message: 'Bakiye sunucusuna bağlanılamadı.',
  };
}

export async function triggerAutoSmsIfEnabled(
  eventType: 'ticketCreated' | 'ticketReady' | 'ticketDelivered' | 'ticketApprovalWaiting',
  ticket: ServiceTicket,
  company: CompanyInfo
): Promise<void> {
  const settings = getLocalSmsSettings();
  if (!settings.enabled || !settings.apiKey || !settings.apiHash || !settings.sender) {
    return;
  }

  let shouldSend = false;
  let template = '';

  if (eventType === 'ticketCreated' && settings.autoSendOnCreate) {
    shouldSend = true;
    template = settings.templates.ticketCreated;
  } else if (eventType === 'ticketApprovalWaiting' && settings.autoSendOnApprovalWaiting) {
    shouldSend = true;
    template = settings.templates.ticketApprovalWaiting || settings.templates.ticketPriceApproved;
  } else if (eventType === 'ticketReady' && settings.autoSendOnReady) {
    shouldSend = true;
    template = settings.templates.ticketReady;
  } else if (eventType === 'ticketDelivered' && settings.autoSendOnDelivered) {
    shouldSend = true;
    template = settings.templates.ticketDelivered;
  }

  if (shouldSend && template && ticket.customerPhone) {
    const message = formatSmsTemplate(template, ticket, company);
    if (message.trim()) {
      await sendSmsMessage({
        phone: ticket.customerPhone,
        text: message,
        ticketId: ticket.id,
        customerName: ticket.customerName,
      });
    }
  }
}
